import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button } from '@mui/material';
import CpdSectionHeader from '@/components/ui/CpdSectionHeader';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import loader from '@/assets/img/loader.svg';
import useFormValidation from '@/hooks/useFormValidation';
import { useGetWellsFargoWebhookSubscription } from '@/api/hooks/wells-fargo-settings/useGetWellsFargoWebhookSubscription';
import { useRegisterWellsFargoWebhookSubscription } from '@/api/hooks/wells-fargo-settings/useRegisterWellsFargoWebhookSubscription';
import { useSyncWellsFargoWebhookSubscription } from '@/api/hooks/wells-fargo-settings/useSyncWellsFargoWebhookSubscription';
import { useDeleteWellsFargoWebhookSubscription } from '@/api/hooks/wells-fargo-settings/useDeleteWellsFargoWebhookSubscription';
import { useConfirmation } from '@/context/ConfirmationContext';
import WellsFargoWebhookSubscriptionDetails from './WellsFargoWebhookSubscriptionDetails';
import WellsFargoWebhookRegisterForm from './WellsFargoWebhookRegisterForm';

const T = 'organizations.update.wellsFargoWebhook';

const toFailureMessage = (error, t, fallbackKey) =>
  error.response?.data?.message ?? t(`${T}.${fallbackKey}`);

const WellsFargoWebhookSubscriptionSection = ({ organizationId }) => {
  const { t } = useTranslation();
  const {
    data,
    setData,
    loading,
    error: loadError,
    refetch,
  } = useGetWellsFargoWebhookSubscription(organizationId);
  const {
    registerSubscription,
    loading: registering,
  } = useRegisterWellsFargoWebhookSubscription();
  const {
    syncSubscription,
    loading: syncing,
  } = useSyncWellsFargoWebhookSubscription();
  const {
    deleteSubscription,
    loading: deleting,
  } = useDeleteWellsFargoWebhookSubscription();
  const { requestConfirmation } = useConfirmation();
  const [pathKey, setPathKey] = useState('');
  const [failureMessage, setFailureMessage] = useState(null);
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearFieldError,
    clearAllErrors,
  } = useFormValidation();

  const readOnly = data?.read_only ?? false;

  const changePathKey = (value) => {
    setPathKey(value);
    clearFieldError('path_key');
  };

  const register = async () => {
    clearAllErrors();
    setFailureMessage(null);
    try {
      const response = await registerSubscription(organizationId, {
        path_key: pathKey,
      });
      setData(response.data);
      setPathKey('');
      toast.success(t(`${T}.registered`));
    } catch (error) {
      if (error.response?.status === 400) {
        setBackendErrors(error.response.data);
        return;
      }
      setFailureMessage(toFailureMessage(error, t, 'registerFailed'));
    }
  };

  const sync = async () => {
    setFailureMessage(null);
    try {
      const response = await syncSubscription(organizationId);
      setData(response.data);
      if (response.data.subscription) {
        toast.success(t(`${T}.synced`));
        return;
      }
      toast.info(t(`${T}.syncedDeleted`));
    } catch (error) {
      setFailureMessage(toFailureMessage(error, t, 'syncFailed'));
    }
  };

  const remove = async () => {
    setFailureMessage(null);
    try {
      const response = await deleteSubscription(organizationId);
      setData(response.data);
      toast.success(t(`${T}.deleted`));
    } catch (error) {
      setFailureMessage(toFailureMessage(error, t, 'deleteFailed'));
    }
  };

  const confirmRemove = () => {
    requestConfirmation(t(`${T}.deleteConfirm`), remove);
  };

  const header = <CpdSectionHeader title={t(`${T}.title`)} />;

  if (loading) {
    return (
      <div className='loaders' style={{ marginTop: '20px' }}>
        <img alt='loader' src={loader} />
      </div>
    );
  }

  if (loadError) {
    return (
      <Box>
        {header}
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={refetch}>
              {t(`${T}.retryButton`)}
            </Button>
          }>
          {t(`${T}.loadFailed`)}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {header}

      <FormErrorSummary errors={generalErrors} />
      {readOnly && (
        <Alert severity='info' sx={{ mb: 2 }}>
          {t(`${T}.readOnly`)}
        </Alert>
      )}
      {failureMessage && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {failureMessage}
        </Alert>
      )}

      {data?.subscription && (
        <WellsFargoWebhookSubscriptionDetails
          subscription={data.subscription}
          disabled={readOnly || syncing || deleting}
          syncing={syncing}
          deleting={deleting}
          onSync={sync}
          onDelete={confirmRemove}
        />
      )}
      {!data?.subscription && (
        <WellsFargoWebhookRegisterForm
          pathKey={pathKey}
          pathKeyError={fieldErrors.path_key}
          disabled={readOnly || registering}
          registering={registering}
          onPathKeyChange={changePathKey}
          onRegister={register}
        />
      )}
    </Box>
  );
};

export default WellsFargoWebhookSubscriptionSection;
